import { html, LitElement, nothing, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import { IconDefaultType, ICON_FAMILY_NAVIGATION } from "@slhuiicon/icon-core";
import { Translatable } from "@slhuinav/i18n/src/mixins/translatable";
import { ApplicationItemInterface } from "@slhuinav/base";
import { ifDefined } from "lit/directives/if-defined.js";
import { SlhNavigationItemTypeEnum } from "@slhuinav/item/src/item.type";
import { SlhNavigationSidebarState } from "./sidebar.type";
import * as SidebarConstants from "./sidebar.constants";
import { style } from "./assets/sidebar.generated-style";

/**
 * Sidebar component
 *
 * @CustomElement slh-nav-sidebar
 *
 * @cssprop --slh-default-font-family - Sidebar font family.
 * @cssprop --slh-base-xxs-font-size - Sidebar group title font size.
 * @cssprop --slh-base-bold-font-weight - Sidebar group title font weight.
 * @cssprop --slh-base-xxs-line-height - Sidebar group title line-height.
 * @cssprop --slh-color-neutral-shade-00 - Sidebar background color.
 * @cssprop --slh-color-neutral-shade-60 - Sidebar group title color.
 * @cssprop --slh-color-primary - Sidebar expanded icon collapse fill color.
 * @cssprop --slh-color-outline - Sidebar links focus color.
 */
@customElement("slh-nav-sidebar")
export default class SlhNavigationSidebar extends Translatable(LitElement) {
  static styles = style;

  /**
   * Sidebar HTML state attribute
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  state: SlhNavigationSidebarState = SlhNavigationSidebarState.HIDDEN;

  /**
   * Sidebar items
   */
  @property({
    type: Array,
    attribute: true,
    reflect: true,
  })
  applications?: Array<ApplicationItemInterface> = [];

  /**
   * Home
   */
  @property({
    type: Object,
    attribute: "home",
    reflect: true,
  })
  home?: ApplicationItemInterface;

  /**
   * Suppress link activation (useful when custom item click handling is required)
   */
  @property({
    type: Boolean,
    attribute: "prevent-default",
    reflect: true,
  })
  preventDefault: boolean = false;

  /**
   * Close sidebar icon
   */
  @query(".sidebar-icon")
  closeIconElement!: HTMLElement;

  onOutsideClick: (this: Document, e: MouseEvent) => any = () => {};

  onKeyboardDown: (this: Document, e: KeyboardEvent) => any = () => {};

  onToggle: (e: Event) => any = () => {};

  onResize: (e: Event) => void = () => {};

  private resizeTimeout: number | null = null;

  @state()
  protected activeItem?: ApplicationItemInterface;

  @state()
  loaded: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("sidebar-wrapper");
    this.onToggle = this.toggle.bind(this);
    window.addEventListener(
      SidebarConstants.EVENT_SIDEBAR_TOGGLE,
      this.onToggle,
    );
    this.onResize = this.resizeWidth.bind(this);
    window.addEventListener("resize", this.onResize);
  }

  disconnectedCallback() {
    window.removeEventListener(
      SidebarConstants.EVENT_SIDEBAR_TOGGLE,
      this.onToggle,
    );
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener(
      SidebarConstants.CLICK_EVENT,
      this.onOutsideClick,
    );
    document.removeEventListener(
      SidebarConstants.KEYDOWN_EVENT,
      this.onKeyboardDown,
    );
    super.disconnectedCallback();
  }

  /**
   * Derives `loaded` flag and `activeItem` before render so the correct
   * active state is available in the template without an extra render cycle.
   * `activeItem` is a `@state()` so assignments here are batched into the
   * current render and do not schedule an additional update.
   */
  protected willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has("home")) {
      this.loaded = true;
      if (this.home?.active) {
        this.activeItem = this.home;
      }
    }
    if (changedProperties.has("applications")) {
      this.loaded = true;
      const activeApp = this.applications?.find((item) => item.active);
      if (activeApp) {
        this.activeItem = activeApp;
      }
    }
  }

  /**
   * Render sidebar top
   * @returns {TemplateResult}
   */
  renderTop(): TemplateResult {
    return html`<div class="sidebar-top">
      ${this.loaded
        ? html`<a
            href="#"
            class="sidebar-icon"
            @keydown="${this.sidebarIconKeydown.bind(this)}"
            @click="${this.sidebarIconClick.bind(this)}"
          >
            <slh-icon type="${IconDefaultType.ICO_HUB_NAV}"></slh-icon>
          </a>`
        : html`${this.renderSkeletonItem()}`}
    </div>`;
  }

  private sidebarIconClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  renderSkeletonItem(): TemplateResult {
    return html`<slh-nav-item class="item-skeleton" loading>
      ${this.trans("sidebar.loading")}
    </slh-nav-item>`;
  }

  /**
   * Render sidebar items
   */
  renderItems(): TemplateResult {
    return html`<div class="sidebar-items-holder">
      ${this.renderItem(this.home, SlhNavigationItemTypeEnum.HOME)}
      ${this.applications?.map((item: ApplicationItemInterface) => {
        return html`<div class="sidebar-item-holder">
          ${this.renderItem(item, SlhNavigationItemTypeEnum.APP)}
        </div>`;
      })}
    </div>`;
  }

  /**
   * Render sidebar item
   */
  renderItem(
    item?: ApplicationItemInterface,
    typeOverride?: string,
  ): TemplateResult {
    if (item === null || item === undefined) {
      return html`${nothing}`;
    }
    return html`<slh-nav-item
      id="${item.identifier}"
      href="${item.url}"
      ?active="${this.activeItem === item}"
      ?prevent-default="${this.preventDefault}"
      prominent="true"
      type="${ifDefined(typeOverride ?? item.type)}"
    >
      ${item.title}
      ${item.icon !== null && item.icon !== undefined
        ? html`<slh-icon
            slot="icon"
            family="${ICON_FAMILY_NAVIGATION}"
            type="${item.icon}"
          ></slh-icon>`
        : html``}
    </slh-nav-item>`;
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  renderClasses(): ClassInfo {
    return {
      expanded: this.state === SlhNavigationSidebarState.EXPANDED,
      hidden: this.state === SlhNavigationSidebarState.HIDDEN,
    };
  }

  /**
   * @returns {string} render html template
   */
  render(): TemplateResult {
    return html`<nav class="sidebar-holder ${classMap(this.renderClasses())}">
      ${this.renderTop()} ${this.renderItems()}
    </nav>`;
  }

  /**
   * Expand sidebar
   */
  private toggle(): void {
    if (this.state === SlhNavigationSidebarState.EXPANDED) {
      this.hide();
      if (this.isMobile()) {
        this.setBodyStyle(
          SidebarConstants.BODY_ELEMENT_AUTO_HEIGHT,
          SidebarConstants.BODY_ELEMENT_AUTO_OVERFLOW,
        );
      }
    } else {
      this.state = SlhNavigationSidebarState.EXPANDED;
      if (this.isMobile()) {
        this.setBodyStyle(
          SidebarConstants.BODY_ELEMENT_HIDDEN_HEIGHT,
          SidebarConstants.BODY_ELEMENT_HIDDEN_OVERFLOW,
        );
      }

      setTimeout(() => {
        this.closeIconElement?.focus();
      }, 1);

      setTimeout(() => {
        this.onOutsideClick = this.outsideClick.bind(this);
        document.addEventListener(
          SidebarConstants.CLICK_EVENT,
          this.onOutsideClick,
        );
        this.onKeyboardDown = this.onKeydown.bind(this);
        document.addEventListener(
          SidebarConstants.KEYDOWN_EVENT,
          this.onKeyboardDown,
        );
      }, 1);
    }
  }

  /**
   * Show sidebar container on Enter or Space key down on sidebar icon
   */
  private sidebarIconKeydown(event: KeyboardEvent): void {
    const { code } = event;
    if (code === "Enter" || code === "NumpadEnter" || code === "Space") {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  /**
   * Hide sidebar container on Escape key down on sidebar icon
   */
  private onKeydown(event: KeyboardEvent): void {
    const { code } = event;
    if (code === "Escape") {
      this.hide();
    }
  }

  /**
   * Hide sidebar container on outside click
   */
  private outsideClick(event: MouseEvent): void {
    if (!event.composedPath().includes(this.renderRoot)) {
      this.hide();
    }
  }

  /**
   * Hide sidebar
   */
  private hide(): void {
    this.state = SlhNavigationSidebarState.HIDDEN;

    const customEvent = new CustomEvent(SidebarConstants.EVENT_SIDEBAR_CLOSED, {
      detail: { focused: true },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(customEvent);

    document.removeEventListener(
      SidebarConstants.CLICK_EVENT,
      this.onOutsideClick,
    );
    document.removeEventListener(
      SidebarConstants.KEYDOWN_EVENT,
      this.onKeyboardDown,
    );
  }

  public setActiveItem(item?: ApplicationItemInterface): void {
    this.activeItem = item;
  }

  private setBodyStyle(height: string, overflow: string) {
    const bodyElement: HTMLBodyElement | null = document.querySelector("body");
    if (bodyElement) {
      bodyElement.style.height = height;
      bodyElement.style.overflow = overflow;
    }
  }

  /**
   * Adjusts event handlers on breakpoint change
   */
  private resize(): void {
    this.setBodyStyle(
      SidebarConstants.BODY_ELEMENT_AUTO_HEIGHT,
      SidebarConstants.BODY_ELEMENT_AUTO_OVERFLOW,
    );
    if (this.isMobile() && this.state === SlhNavigationSidebarState.EXPANDED) {
      this.setBodyStyle(
        SidebarConstants.BODY_ELEMENT_HIDDEN_HEIGHT,
        SidebarConstants.BODY_ELEMENT_HIDDEN_OVERFLOW,
      );
    }
  }

  private resizeWidth() {
    if (this.resizeTimeout) {
      window.clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = window.setTimeout(
      this.resize.bind(this),
      SidebarConstants.RESIZE_TRESHOLD,
    );
  }

  private isMobile() {
    return window.innerWidth <= SidebarConstants.MOBILE_BREAK_POINT;
  }
}
